import type { InventoryItem, Vendor, PurchaseOrder, GoodsReceivedNote, StockLedgerEntry } from '../types';

let itemDatabase: InventoryItem[] = [
  {
    id: 'item-1',
    item_code: 'ITM-001',
    name: 'Surgical Gloves',
    category: 'medical_equipment',
    description: 'Disposable latex surgical gloves',
    unit_of_measure: 'box',
    reorder_level: 50,
    current_stock: 25,
    unit_cost: 15.99,
    location: 'Storage Room A',
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'item-2',
    item_code: 'ITM-002',
    name: 'Office Paper',
    category: 'office_supplies',
    description: 'A4 white printing paper',
    unit_of_measure: 'ream',
    reorder_level: 20,
    current_stock: 45,
    unit_cost: 8.50,
    location: 'Office Storage',
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'item-3',
    item_code: 'ITM-003',
    name: 'Disinfectant Spray',
    category: 'cleaning_supplies',
    description: 'Hospital-grade disinfectant',
    unit_of_measure: 'bottle',
    reorder_level: 30,
    current_stock: 15,
    unit_cost: 12.75,
    location: 'Cleaning Supplies',
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
];

let vendorDatabase: Vendor[] = [
  {
    id: 'vendor-1',
    vendor_code: 'VEN-001',
    name: 'MedSupply Corp',
    contact_person: 'John Smith',
    phone: '+1-555-0123',
    email: 'orders@medsupply.com',
    address: '123 Medical Ave, Healthcare City, HC 12345',
    tax_id: 'TAX-123456789',
    payment_terms: 'Net 30',
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'vendor-2',
    vendor_code: 'VEN-002',
    name: 'Office Solutions Ltd',
    contact_person: 'Sarah Johnson',
    phone: '+1-555-0124',
    email: 'sales@officesolutions.com',
    address: '456 Business Blvd, Commerce City, CC 67890',
    tax_id: 'TAX-987654321',
    payment_terms: 'Net 15',
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
];

let poDatabase: PurchaseOrder[] = [
  {
    id: 'po-1',
    po_number: 'PO-2024-001',
    vendor_id: 'vendor-1',
    order_date: '2024-01-15',
    expected_delivery_date: '2024-01-25',
    status: 'sent',
    subtotal: 500.00,
    tax_amount: 40.00,
    total: 540.00,
    notes: 'Urgent order for surgical supplies',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    items: [
      {
        id: 'po-item-1',
        po_id: 'po-1',
        item_id: 'item-1',
        quantity_ordered: 20,
        unit_price: 15.99,
        total_price: 319.80,
        created_at: new Date().toISOString()
      },
      {
        id: 'po-item-2',
        po_id: 'po-1',
        item_id: 'item-3',
        quantity_ordered: 15,
        unit_price: 12.75,
        total_price: 191.25,
        created_at: new Date().toISOString()
      }
    ]
  }
];

let grnDatabase: GoodsReceivedNote[] = [];
let stockLedgerDatabase: StockLedgerEntry[] = [];

let nextItemId = 4;
let nextVendorId = 3;
let nextPOId = 2;
let nextGRNId = 1;
let nextStockLedgerId = 1;

export const inventoryService = {
  async getAll(): Promise<InventoryItem[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [...itemDatabase];
  },

  async getById(id: string): Promise<InventoryItem | null> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return itemDatabase.find(item => item.id === id) || null;
  },

  async create(itemData: Omit<InventoryItem, 'id' | 'item_code' | 'created_at' | 'updated_at'>): Promise<InventoryItem> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const lastItemCode = itemDatabase
      .map(i => parseInt(i.item_code.split('-')[1]))
      .sort((a, b) => b - a)[0] || 0;
    
    const newItem: InventoryItem = {
      ...itemData,
      id: `item-${nextItemId++}`,
      item_code: `ITM-${String(lastItemCode + 1).padStart(3, "0")}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    itemDatabase.push(newItem);
    return newItem;
  },

  async update(id: string, updates: Partial<InventoryItem>): Promise<InventoryItem> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const index = itemDatabase.findIndex(item => item.id === id);
    if (index === -1) {
      throw new Error('Item not found');
    }
    
    const updatedItem = {
      ...itemDatabase[index],
      ...updates,
      updated_at: new Date().toISOString()
    };
    
    itemDatabase[index] = updatedItem;
    return updatedItem;
  },

  async updateStock(id: string, newStock: number): Promise<InventoryItem> {
    return this.update(id, { current_stock: newStock });
  }
};

export const vendorService = {
  async getAll(): Promise<Vendor[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [...vendorDatabase];
  },

  async getById(id: string): Promise<Vendor | null> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return vendorDatabase.find(vendor => vendor.id === id) || null;
  },

  async create(vendorData: Omit<Vendor, 'id' | 'vendor_code' | 'created_at' | 'updated_at'>): Promise<Vendor> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const lastVendorCode = vendorDatabase
      .map(v => parseInt(v.vendor_code.split('-')[1]))
      .sort((a, b) => b - a)[0] || 0;
    
    const newVendor: Vendor = {
      ...vendorData,
      id: `vendor-${nextVendorId++}`,
      vendor_code: `VEN-${String(lastVendorCode + 1).padStart(3, "0")}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    vendorDatabase.push(newVendor);
    return newVendor;
  },

  async update(id: string, updates: Partial<Vendor>): Promise<Vendor> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const index = vendorDatabase.findIndex(vendor => vendor.id === id);
    if (index === -1) {
      throw new Error('Vendor not found');
    }
    
    const updatedVendor = {
      ...vendorDatabase[index],
      ...updates,
      updated_at: new Date().toISOString()
    };
    
    vendorDatabase[index] = updatedVendor;
    return updatedVendor;
  }
};

export const purchaseOrderService = {
  async getAll(): Promise<PurchaseOrder[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [...poDatabase];
  },

  async getById(id: string): Promise<PurchaseOrder | null> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return poDatabase.find(po => po.id === id) || null;
  },

  async create(poData: Omit<PurchaseOrder, 'id' | 'po_number' | 'created_at' | 'updated_at'>): Promise<PurchaseOrder> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const newPO: PurchaseOrder = {
      ...poData,
      id: `po-${nextPOId++}`,
      po_number: `PO-${Date.now()}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    poDatabase.push(newPO);
    return newPO;
  },

  async update(id: string, updates: Partial<PurchaseOrder>): Promise<PurchaseOrder> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const index = poDatabase.findIndex(po => po.id === id);
    if (index === -1) {
      throw new Error('Purchase order not found');
    }
    
    const updatedPO = {
      ...poDatabase[index],
      ...updates,
      updated_at: new Date().toISOString()
    };
    
    poDatabase[index] = updatedPO;
    return updatedPO;
  }
};

export const grnService = {
  async getAll(): Promise<GoodsReceivedNote[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [...grnDatabase];
  },

  async create(grnData: Omit<GoodsReceivedNote, 'id' | 'grn_number' | 'created_at' | 'updated_at'>): Promise<GoodsReceivedNote> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const newGRN: GoodsReceivedNote = {
      ...grnData,
      id: `grn-${nextGRNId++}`,
      grn_number: `GRN-${Date.now()}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    grnDatabase.push(newGRN);
    
    for (const item of newGRN.items) {
      const inventoryItem = itemDatabase.find(i => i.id === item.item_id);
      if (inventoryItem && item.condition === 'good') {
        inventoryItem.current_stock += item.quantity_received;
        inventoryItem.updated_at = new Date().toISOString();
        
        const stockEntry: StockLedgerEntry = {
          id: `stock-${nextStockLedgerId++}`,
          item_id: item.item_id,
          transaction_type: 'purchase',
          reference_id: newGRN.id,
          reference_type: 'grn',
          quantity_in: item.quantity_received,
          quantity_out: 0,
          balance: inventoryItem.current_stock,
          unit_cost: inventoryItem.unit_cost,
          transaction_date: newGRN.received_date,
          notes: `Received via GRN ${newGRN.grn_number}`,
          created_at: new Date().toISOString()
        };
        
        stockLedgerDatabase.push(stockEntry);
      }
    }
    
    return newGRN;
  }
};
